/**
 * Series API Service
 * Handles all API calls for course series (creator-side grouping)
 */

import { apiClient as api } from './apiClient';
import type { Course } from './coursesService';

import { API_BASE_URL } from '../config';
const SERIES_API_BASE = `${API_BASE_URL}/api/v1/series`;

// Types for Series entities
export interface Series {
  id: string;
  creatorId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  titles?: Record<string, string>;
  descriptions?: Record<string, string>;
  courses?: Course[];
  courseCount: number;
  isPublished: boolean;
  creator?: {
    id: string;
    fullName?: string;
    username?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateSeriesDto {
  title: string;
  description?: string;
  thumbnailUrl?: string;
  titles?: Record<string, string>;
  descriptions?: Record<string, string>;
}

export interface UpdateSeriesDto {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  titles?: Record<string, string>;
  descriptions?: Record<string, string>;
  isPublished?: boolean;
}

export interface AddCourseToSeriesDto {
  courseId: string;
  order?: number;
}

export interface ReorderCoursesDto {
  courseIds: string[];
}

// API Functions
export const seriesService = {
  /**
   * Create a new series
   */
  async create(data: CreateSeriesDto): Promise<Series> {
    const response = await api.post<Series>(SERIES_API_BASE, data);
    return response.data;
  },

  /**
   * Get all public series
   */
  async getAll(): Promise<Series[]> {
    const response = await api.get<Series[]>(SERIES_API_BASE);
    return response.data;
  },

  /**
   * Get current creator's series
   */
  async getMySeries(): Promise<Series[]> {
    const response = await api.get<Series[]>(`${SERIES_API_BASE}/me`);
    return response.data;
  },

  /**
   * Get a single series by ID
   */
  async getById(id: string): Promise<Series> {
    const response = await api.get<Series>(`${SERIES_API_BASE}/${id}`);
    return response.data;
  },

  /**
   * Update a series
   */
  async update(id: string, data: UpdateSeriesDto): Promise<Series> {
    const response = await api.patch<Series>(`${SERIES_API_BASE}/${id}`, data);
    return response.data;
  },

  /**
   * Delete a series
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${SERIES_API_BASE}/${id}`);
  },

  /**
   * Add a course to a series
   */
  async addCourse(seriesId: string, data: AddCourseToSeriesDto): Promise<Series> {
    const response = await api.post<Series>(
      `${SERIES_API_BASE}/${seriesId}/courses`,
      data
    );
    return response.data;
  },

  /**
   * Remove a course from a series
   */
  async removeCourse(seriesId: string, courseId: string): Promise<Series> {
    const response = await api.delete<Series>(
      `${SERIES_API_BASE}/${seriesId}/courses/${courseId}`
    );
    return response.data;
  },

  /**
   * Reorder courses in a series
   */
  async reorderCourses(seriesId: string, data: ReorderCoursesDto): Promise<Series> {
    const response = await api.patch<Series>(
      `${SERIES_API_BASE}/${seriesId}/reorder`,
      data
    );
    return response.data;
  },
};

export default seriesService;
